import {
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { RotateCw, Upload, X } from "lucide-react";
import { memo, useRef, useState } from "react";
import { Button } from "components/ds";
import type { EvidenceFile } from "state/members/accessReviewsEvidence";
import { LunaMark } from "components/ds/LunaMark";
import { formatFileSize } from "../reviewLogic";
import EvidenceCard from "./EvidenceCard";

/**
 * EvidencePanel — the evidence split-panel (PLO-4818, design D). The panel owns
 * the WHOLE evidence flow now (no separate modal): it lists uploaded evidence,
 * carries the dashed dropzone / file picker that IS the upload entry, renders
 * in-flight + failed uploads inline (retry/remove on the card), and gates
 * "Analyse with Luna" behind having at least one file.
 *
 * The actual upload mutation lives in ReviewDetail; this panel just collects the
 * files (click-to-browse or drag-drop) and reports them up via onUpload.
 */

// A transient upload the panel renders as its own card until the mutation
// resolves (success removes it — the persisted card takes over) or fails.
export interface EvidenceUpload {
  id: string;
  name: string;
  size: number;
  status: "uploading" | "error";
  error?: string;
  file: File;
}

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.csv,.xlsx";

export interface EvidencePanelProps {
  files: EvidenceFile[];
  uploads: EvidenceUpload[];
  isDownloading?: boolean;
  isDeleting?: boolean;
  canAnalyse: boolean;
  hasExistingAnalysis: boolean;
  readOnly?: boolean;
  // Embedded uses (the attestation page) pass their own intro copy, drop the
  // per-account-set Luna action, and have no panel to close.
  description?: string;
  showAnalyse?: boolean;
  onUpload: (files: File[]) => void;
  onRetryUpload: (id: string) => void;
  onDismissUpload: (id: string) => void;
  onAnalyse: () => void;
  onDownload: (fileId: string) => void;
  onDelete: (fileId: string, filename: string) => void;
  onClose?: () => void;
}

// In-flight / failed upload card — mirrors EvidenceCard's chrome.
const UploadCard = memo(
  ({
    upload,
    readOnly,
    onRetry,
    onDismiss,
  }: {
    upload: EvidenceUpload;
    readOnly?: boolean;
    onRetry: (id: string) => void;
    onDismiss: (id: string) => void;
  }) => {
    const failed = upload.status === "error";
    const retry = () => {
      if (readOnly) return;
      onRetry(upload.id);
    };
    return (
      <HStack
        spacing="sm"
        p="sm"
        borderRadius="component.2xl"
        border="1px solid"
        borderColor={failed ? "error.200" : "border.default"}
        bg="bg.container"
        justify="space-between"
      >
        <HStack spacing="sm" flex="1" minW={0}>
          <Flex
            w="32px"
            h="32px"
            borderRadius="component.md"
            bg={failed ? "error.50" : "bg.section"}
            align="center"
            justify="center"
            flexShrink={0}
          >
            {failed ? (
              <Icon as={X} boxSize="16px" color="error.600" />
            ) : (
              <Spinner size="sm" color="text.body.tertiary" />
            )}
          </Flex>
          <Box minW={0} flex="1">
            <Text
              fontSize="smd"
              fontWeight={500}
              color="text.body.primary"
              noOfLines={1}
            >
              {upload.name}
            </Text>
            {failed ? (
              <Text fontSize="xs" fontWeight={500} color="error.600">
                Failed to upload
              </Text>
            ) : (
              <Text fontSize="xs" color="text.body.tertiary">
                Uploading · {formatFileSize(upload.size)}
              </Text>
            )}
          </Box>
        </HStack>

        {failed && (
          <HStack spacing="1xs" flexShrink={0}>
            <Tooltip label="Retry" hasArrow placement="top">
              <Button
                aria-label="Retry upload"
                icon={<Icon as={RotateCw} boxSize={4} />}
                size="sm"
                variant="subtle"
                tone="neutral"
                onClick={retry}
                isDisabled={readOnly}
              />
            </Tooltip>
            <Tooltip label="Remove" hasArrow placement="top">
              <Button
                aria-label="Remove upload"
                icon={<Icon as={X} boxSize={4} />}
                size="sm"
                variant="subtle"
                tone="neutral"
                onClick={() => onDismiss(upload.id)}
              />
            </Tooltip>
          </HStack>
        )}
      </HStack>
    );
  },
);
UploadCard.displayName = "UploadCard";

const EvidencePanel = memo(
  ({
    files,
    uploads,
    isDownloading,
    isDeleting,
    canAnalyse,
    hasExistingAnalysis,
    readOnly,
    description,
    showAnalyse = true,
    onUpload,
    onRetryUpload,
    onDismissUpload,
    onAnalyse,
    onDownload,
    onDelete,
    onClose,
  }: EvidencePanelProps) => {
    const empty = files.length === 0 && uploads.length === 0;
    // A read-only panel may still open an existing analysis, never start one.
    const analyseLocked = readOnly && !hasExistingAnalysis;
    const analyse = () => {
      if (analyseLocked) return;
      onAnalyse();
    };
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const pickFiles = (list: FileList | null) => {
      if (!list || list.length === 0) return;
      onUpload(Array.from(list));
    };

    return (
      <Flex
        direction="column"
        h="100%"
        bg="bg.container"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="component.2xl"
        overflow="hidden"
      >
        {/* header */}
        <Flex
          align="center"
          justify="space-between"
          gap="sm"
          px="lg"
          py="md"
          flex="none"
          borderBottom="1px solid"
          borderColor="border.default"
        >
          <VStack align="flex-start" spacing="2px" minW={0}>
            <Text
              fontFamily="heading"
              fontSize="lg"
              fontWeight={500}
              color="text.body.primary"
            >
              Evidence
            </Text>
            <Text fontSize="xs" color="text.body.tertiary">
              {files.length} {files.length === 1 ? "file" : "files"}
            </Text>
          </VStack>
          {onClose && (
          <Button
            aria-label="Close evidence panel"
            icon={<Icon as={X} boxSize={4} />}
            size="sm"
            variant="subtle"
            tone="neutral"
            onClick={onClose}
          />
          )}
        </Flex>

        {/* body */}
        <VStack
          align="stretch"
          flex="1"
          overflowY="auto"
          spacing="md"
          px="lg"
          py="lg"
        >
          <Text fontSize="smd" color="text.body.tertiary" lineHeight="1.5">
            {description ??
              "Upload screenshots or CSV exports from the system under review. Luna will cross-reference them against this review and flag any discrepancies. PDF and XLSX files are kept as evidence, but Luna does not read them."}
          </Text>

          {files.map((file) => (
            <EvidenceCard
              key={file.id}
              file={file}
              isDownloading={isDownloading}
              isDeleting={isDeleting}
              onDownload={(f) => onDownload(f.id)}
              onDelete={
                readOnly ? undefined : (f) => onDelete(f.id, f.filename)
              }
            />
          ))}

          {uploads.map((u) => (
            <UploadCard
              key={u.id}
              upload={u}
              readOnly={readOnly}
              onRetry={onRetryUpload}
              onDismiss={onDismissUpload}
            />
          ))}

          {!readOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                onChange={(e) => {
                  pickFiles(e.target.files);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                style={{ display: "none" }}
              />
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={empty ? "sm" : "2xs"}
                textAlign="center"
                cursor="pointer"
                px="lg"
                py={empty ? "xl" : "md"}
                minH={empty ? "180px" : undefined}
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={dragging ? "border.strong" : "border.default"}
                borderRadius="component.lg"
                bg={dragging ? "bg.hover" : "bg.section"}
                color="text.body.tertiary"
                transition="border-color 0.12s ease, background 0.12s ease"
                _hover={{ borderColor: "border.strong", bg: "bg.hover" }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  pickFiles(e.dataTransfer.files);
                }}
              >
                <Icon as={Upload} boxSize={empty ? "22px" : "16px"} />
                <Text
                  fontSize={empty ? "md" : "smd"}
                  fontWeight={500}
                  color="text.body.primary"
                >
                  {empty
                    ? "Drop files here or click to browse"
                    : "Add more files"}
                </Text>
                {empty && (
                  <Text fontSize="xs" color="text.body.tertiary">
                    Supports PNG, JPG, PDF, CSV, XLSX · 4MB each
                  </Text>
                )}
              </Flex>
            </>
          )}
        </VStack>

        {/* footer — Analyse with Luna lives INSIDE the panel */}
        {showAnalyse && (
        <Box
          px="lg"
          py="md"
          flex="none"
          borderTop="1px solid"
          borderColor="border.default"
        >
          <Tooltip
            label="Add evidence to analyse"
            isDisabled={!empty}
            placement="top"
            hasArrow
          >
            <Box as="span" display="block">
              <Button
                variant="outline"
                tone="luna"
                size="md"
                style={{ width: "100%" }}
                onClick={analyse}
                isDisabled={empty || !canAnalyse || analyseLocked}
                leadingIcon={<LunaMark size={18} />}
              >
                {hasExistingAnalysis ? "View analysis" : "Analyse with Luna"}
              </Button>
            </Box>
          </Tooltip>
        </Box>
        )}
      </Flex>
    );
  },
);

EvidencePanel.displayName = "EvidencePanel";

export default EvidencePanel;
